import { useId, useState, type FormEvent } from "react";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import { RegisterQuantityInputs } from "./RegisterQuantityInputs";
import type { Account, AccountKind, Guid, Vendor } from "@/api/types";
import "./RegisterEntryDialog.css";

/**
 * ef-042-5, reshaped ef-056-2 (ADR 0010 §1/§5, D5) — the register's own entry composer,
 * now the register list's own row zero rather than a card above it. Named `*Dialog` per
 * the original build brief's file list, but rendered INLINE as the first `<li>` of
 * `Books.tsx`'s `<ul aria-label="Register">`, never an overlay or a standalone card:
 * `books-register.spec.ts`'s own `open()` helper never clicks a trigger before filling
 * `getByLabel(/category/i)` etc., so Simple's fields have to already be on screen the
 * moment the register renders.
 *
 * ONE FORM, THREE MODES — never three separate dialogs. `PostRegisterEntryRequest` only
 * has two wire shapes (Simple / Explicit — `BooksRegisterContracts.cs`), and Check vs.
 * Deposit isn't a mode the farmer picks at all: both are the Simple shape, and which one
 * an entry becomes is read back off the CATEGORY's own account kind
 * (`BooksRegisterRepository.ClassifyType` — income sorts to "Deposit", everything else
 * that isn't a second instrument sorts to "Check"). So "Check/Deposit" is one mode here,
 * picking any category account does the rest. Transfer and Split both post the Explicit
 * shape, with lines signed by each account's own natural balance side
 * (`entry_generate_lines`, db/migrations/0014: asset/expense increase on debit,
 * income/liability/equity increase on credit) — never a client guess at Check vs. Deposit
 * vs. Transfer, which is why none of the three modes below sends its own `type`.
 *
 * DISCLOSED TRANSFER/SPLIT (D5) — Simple stays the always-visible default (mock row-zero
 * shape); the `mode-switch` group that reaches Transfer/Split is gated behind a "More
 * ways to record" toggle (`books-register.spec.ts`'s own `disclose()` helper, exact
 * accessible name `getByRole("button", { name: /more ways to record/i })`). A ONE-WAY
 * reveal per the wave's own open question #3 — once shown, the mode buttons stay shown
 * (the toggle itself is never removed either, so `aria-expanded` stays an honest, stable
 * signal rather than a control that vanishes the moment it's used). The toggle is also
 * what `books-shell.spec.ts`'s "first `<li>` contains a Record button" budget and its own
 * phone tap-target test key off — its accessible name contains "record", distinct from
 * the `/^(save|post|submit)( entry)?$/i` submit button every posting test in this file
 * already clicks.
 */

export type RegisterEntryMode = "simple" | "transfer" | "split";

interface SplitLine {
  key: string;
  accountId: string;
  amount: string;
  quantity: string;
  unit: string;
}

interface RegisterEntryDialogProps {
  /** The register's own (asset) account — always the header account of the resulting
   *  entry, never a leg the farmer picks (`PostRegisterEntryRequest`'s own remarks,
   *  Correction #2). */
  registerAccountId: Guid;
  /** Income/Expense/Liability/Equity accounts — the Check/Deposit category picker and
   *  each Split line's own picker. Never includes an Asset account; that's what Transfer
   *  is for. */
  categoryAccounts: Account[];
  /** Every OTHER Asset (instrument) account — Transfer's destination picker. Already
   *  excludes `registerAccountId` itself. */
  transferAccounts: Account[];
  /** ef-042-5 cross-slice: the tenant's vendor pick-list — `useReferenceData`'s own
   *  `vendors` (sourced from `demoApi.getVendors`), passed down rather than fetched again
   *  here, so a vendor created on the Vendors tab shows up the moment the shared hook
   *  refetches (a reload — same trip `books-vendors.spec.ts`'s own cross-slice test
   *  takes), with no second round trip this component would have to keep in sync itself. */
  vendors: Vendor[];
  /** Fires once the write succeeds, with the new entry's id — the caller re-fetches the
   *  register's first page (`useRegister.refresh`) rather than this component guessing at
   *  the server-computed type/runningBalance/red flag itself. */
  onPosted: (id: Guid) => void;
  /** ef-043-9's books-period lock, passed straight through to a `<fieldset disabled>`
   *  wrapping every field/button below (`getByLabel(/category/i)` must be disabled while
   *  a period is locked — `books-register.spec.ts`'s own close-year tests). Moved INSIDE
   *  this component by ef-056-2's reshape: the caller used to wrap the whole (card-shaped)
   *  composer in its own `<fieldset>`, which no longer works now this component returns a
   *  `<li>` — a `<ul>`'s only valid direct children are `<li>`/`<script>`/`<template>`, so
   *  the fieldset has to live inside the `<li>`, around the `<form>`'s own fields. */
  disabled?: boolean;
}

// A plain incrementing counter, not `crypto.randomUUID()` — this key never leaves the
// browser tab (React list identity only, never sent to the API), so it doesn't need the
// iOS-Safari-fallback treatment `offline/outbox.ts` gives an id that DOES ride a request.
let splitLineSeq = 0;
function blankSplitLine(): SplitLine {
  splitLineSeq += 1;
  return { key: `split-${splitLineSeq}`, accountId: "", amount: "", quantity: "", unit: "" };
}

/** A positive, parseable amount — the one shape every mode's `amount` field needs. */
function parsedAmount(raw: string): number | null {
  const n = Number(raw);
  return raw.trim() !== "" && Number.isFinite(n) && n > 0 ? n : null;
}

/** Asset/Expense increase on the debit side; Income/Liability/Equity increase on credit —
 *  `entry_generate_lines` (db/migrations/0014)'s own rule, mirrored client-side so Split's
 *  lines are signed the same way the server would derive them for a Simple entry. */
function naturalSide(kind: AccountKind): "debit" | "credit" {
  return kind === "Asset" || kind === "Expense" ? "debit" : "credit";
}

/** ISO `YYYY-MM-DD` for today — matches what a `<input type="date">` both reads and
 *  writes, and what `PostRegisterEntryRequest.occurredOn` expects on the wire. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RegisterEntryDialog({
  registerAccountId,
  categoryAccounts,
  transferAccounts,
  vendors,
  onPosted,
  disabled = false,
}: RegisterEntryDialogProps) {
  const [mode, setMode] = useState<RegisterEntryMode>("simple");
  // D5 — one-way reveal. Never reset to false: re-hiding Transfer/Split mid-entry is the
  // failure mode the wave's own open question #3 rejected a collapsible toggle over.
  const [moreWaysRevealed, setMoreWaysRevealed] = useState(false);
  const [categoryAccountId, setCategoryAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [memo, setMemo] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [occurredOn, setOccurredOn] = useState(() => todayIso());
  const [splitLines, setSplitLines] = useState<SplitLine[]>([blankSplitLine()]);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const headingId = useId();
  const categoryId = useId();
  const toAccountFieldId = useId();
  const amountId = useId();
  const occurredOnId = useId();
  const memoId = useId();
  const vendorLabelId = useId();
  const vendorListboxId = useId();
  const modeSwitchId = useId();

  const categoryByAccountId = new Map(categoryAccounts.map((a) => [a.id, a] as const));

  function resetComposeFields(): void {
    setCategoryAccountId("");
    setToAccountId("");
    setAmount("");
    setQuantity("");
    setUnit("");
    setMemo("");
    setVendorId("");
    setOccurredOn(todayIso());
    setSplitLines([blankSplitLine()]);
  }

  function switchMode(next: RegisterEntryMode): void {
    if (next === mode) return;
    setMode(next);
    setFormError(null);
    setFieldErrors(null);
  }

  function updateSplitLine(key: string, patch: Partial<Pick<SplitLine, "accountId" | "amount" | "quantity" | "unit">>): void {
    setSplitLines((lines) => lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function addSplitLine(): void {
    setSplitLines((lines) => [...lines, blankSplitLine()]);
  }

  function removeSplitLine(key: string): void {
    // Never drops to zero rows — `books-register.spec.ts`'s own "removing the only split
    // row" test requires the form stay in a visible, forward-usable state; the Remove
    // button itself is hidden once exactly one row is left (below) so this branch is a
    // second guard against the same defect, not the only one.
    setSplitLines((lines) => (lines.length > 1 ? lines.filter((l) => l.key !== key) : lines));
  }

  // Each filled split line's own signed contribution, keyed by its account's natural
  // balance side. A line with no category chosen yet or no parseable amount contributes
  // nothing — it is simply not part of the running total shown below, same as an empty
  // Amount field never being read as zero.
  const splitTotals = splitLines.reduce(
    (acc, line) => {
      const account = categoryByAccountId.get(line.accountId);
      const value = parsedAmount(line.amount);
      if (!account || value === null) return acc;
      const side = naturalSide(account.kind);
      return side === "debit"
        ? { ...acc, debit: acc.debit + value }
        : { ...acc, credit: acc.credit + value };
    },
    { debit: 0, credit: 0 },
  );
  const splitDistributed = splitTotals.debit + splitTotals.credit;
  // What the register's OWN leg would need to be to balance the lines entered so far —
  // zero exactly when every filled line's side happens to cancel out, which Split never
  // submits (a balancing amount of zero fails entry_line's own `amount > 0` CHECK).
  const registerLegAmount = Math.abs(splitTotals.debit - splitTotals.credit);

  const allSplitLinesFilled = splitLines.every(
    (l) => categoryByAccountId.has(l.accountId) && parsedAmount(l.amount) !== null,
  );
  // A split needs more than one category to actually be a split — one filled line is
  // indistinguishable from Check/Deposit and is deliberately not submittable here, so a
  // farmer who meant Check/Deposit is nudged back to that mode rather than posting a
  // one-line "split".
  const canSubmitSplit = splitLines.length >= 2 && allSplitLinesFilled && registerLegAmount > 0;

  const canSubmit =
    !saving &&
    (mode === "simple"
      ? categoryAccountId !== "" && parsedAmount(amount) !== null
      : mode === "transfer"
        ? toAccountId !== "" && parsedAmount(amount) !== null
        : canSubmitSplit);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);
    if (!canSubmit) return;

    const quantityProblems: Record<string, string[]> = {};
    function validateQuantity(raw: string, selectedUnit: string, prefix = ""): void {
      if (!raw.trim()) return;
      if (parsedAmount(raw) === null) quantityProblems[`${prefix}quantity`] = ["Enter a quantity greater than zero."];
      if (!selectedUnit) quantityProblems[`${prefix}unit`] = ["Choose a unit."];
    }
    if (mode === "simple") validateQuantity(quantity, unit);
    if (mode === "split") splitLines.forEach((line, i) => validateQuantity(line.quantity, line.unit, `lines[${i}].`));
    if (Object.keys(quantityProblems).length) {
      setFieldErrors(quantityProblems);
      return;
    }

    setSaving(true);
    try {
      // Trimmed client-side too — an all-whitespace memo is "no memo" to the server
      // (`BooksRegisterEndpoints.NormalizeText`) as well, so `undefined` here is what a
      // blank field actually means on the wire, not an empty string the server would
      // otherwise normalize away anyway.
      const trimmedMemo = memo.trim();
      const memoField = trimmedMemo === "" ? undefined : trimmedMemo;
      // Optional on the wire, same shape as memoField above — an unpicked vendor is "no
      // vendor" to the server (`PostRegisterEntryRequest.VendorId`), not an empty guid.
      const vendorField = vendorId === "" ? undefined : vendorId;
      // ef-053-0b — an emptied date field is "let the server default to today", same
      // undefined-over-empty-string convention as memoField/vendorField above.
      const occurredOnField = occurredOn === "" ? undefined : occurredOn;

      if (mode === "simple") {
        const value = parsedAmount(amount)!;
        const { id } = await demoApi.postRegisterEntry({
          registerAccountId,
          categoryAccountId,
          amount: value,
          ...(quantity.trim() ? { quantity: Number(quantity), unit } : {}),
          memo: memoField,
          vendorId: vendorField,
          occurredOn: occurredOnField,
        });
        onPosted(id);
      } else if (mode === "transfer") {
        const value = parsedAmount(amount)!;
        // FROM the register account TO the picked destination — the natural reading of
        // "Transfer to Savings", and the direction `books-register.spec.ts`'s own test
        // exercises. Destination (asset) increases on debit; the register's own leg
        // decreases on credit (db/migrations/0014's natural-balance rule).
        const { id } = await demoApi.postRegisterEntry({
          registerAccountId,
          lines: [
            { accountId: toAccountId, side: "debit", amount: value },
            { accountId: registerAccountId, side: "credit", amount: value },
          ],
          memo: memoField,
          vendorId: vendorField,
          occurredOn: occurredOnField,
        });
        onPosted(id);
      } else {
        const lines = splitLines.map((l) => {
          const account = categoryByAccountId.get(l.accountId)!;
          return { accountId: l.accountId, side: naturalSide(account.kind), amount: parsedAmount(l.amount)!,
            ...(l.quantity.trim() ? { quantity: Number(l.quantity), unit: l.unit } : {}) };
        });
        // The register's own leg takes whichever side balances the lines above — see
        // `registerLegAmount`'s own remarks.
        const registerSide = splitTotals.debit > splitTotals.credit ? "credit" : "debit";
        const { id } = await demoApi.postRegisterEntry({
          registerAccountId,
          lines: [...lines, { accountId: registerAccountId, side: registerSide, amount: registerLegAmount }],
          memo: memoField,
          vendorId: vendorField,
          occurredOn: occurredOnField,
        });
        onPosted(id);
      }

      resetComposeFields();
      setMode("simple");
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not post this entry."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="register-entry" data-state={mode}>
      <form className="register-entry__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
      <fieldset className="register-entry__fieldset" disabled={disabled || saving}>
        <legend id={headingId} className="visually-hidden">
          New entry
        </legend>

        <div className="register-entry__disclosure">
          <button
            type="button"
            className="btn btn--ghost register-entry__more-ways"
            aria-expanded={moreWaysRevealed}
            aria-controls={modeSwitchId}
            onClick={() => setMoreWaysRevealed(true)}
          >
            More ways to record
          </button>
          {moreWaysRevealed && (
            <div id={modeSwitchId} className="register-entry__mode-switch" role="group" aria-label="Entry type">
              <button
                type="button"
                className={`register-entry__mode-btn${mode === "simple" ? " register-entry__mode-btn--active" : ""}`}
                aria-pressed={mode === "simple"}
                onClick={() => switchMode("simple")}
              >
                Check / Deposit
              </button>
              <button
                type="button"
                className={`register-entry__mode-btn${mode === "transfer" ? " register-entry__mode-btn--active" : ""}`}
                aria-pressed={mode === "transfer"}
                onClick={() => switchMode("transfer")}
              >
                Transfer
              </button>
              <button
                type="button"
                className={`register-entry__mode-btn${mode === "split" ? " register-entry__mode-btn--active" : ""}`}
                aria-pressed={mode === "split"}
                onClick={() => switchMode("split")}
              >
                Split
              </button>
            </div>
          )}
        </div>

        {mode === "simple" && (
          <div className="register-entry__row">
            <div className="register-entry__field">
              <label htmlFor={categoryId}>Category</label>
              <select
                id={categoryId}
                value={categoryAccountId}
                onChange={(e) => setCategoryAccountId(e.target.value)}
                aria-invalid={fieldErrors?.categoryAccountId ? true : undefined}
              >
                <option value="">Choose a category</option>
                {categoryAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.number} — {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="register-entry__field">
              <label htmlFor={amountId}>Amount</label>
              <input
                id={amountId}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-invalid={fieldErrors?.amount ? true : undefined}
              />
            </div>
          </div>
        )}

        {mode === "simple" && (
          <RegisterQuantityInputs quantity={quantity} unit={unit} onQuantityChange={setQuantity}
            onUnitChange={setUnit} quantityErrors={fieldErrors?.quantity} unitErrors={fieldErrors?.unit} />
        )}

        {mode === "transfer" && (
          <div className="register-entry__row">
            <div className="register-entry__field">
              <label htmlFor={toAccountFieldId}>To account</label>
              <select
                id={toAccountFieldId}
                value={toAccountId}
                onChange={(e) => setToAccountId(e.target.value)}
                aria-invalid={fieldErrors?.registerAccountId ? true : undefined}
              >
                <option value="">Choose an account</option>
                {transferAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.number} — {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="register-entry__field">
              <label htmlFor={amountId}>Amount</label>
              <input
                id={amountId}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>
        )}

        {mode === "split" && (
          <div className="register-entry__split">
            {splitLines.map((line, index) => (
              <div
                key={line.key}
                className="books-register__split-line"
                role="group"
                aria-label={`Split line ${index + 1}`}
              >
                <div className="register-entry__field">
                  <label htmlFor={`${categoryId}-${line.key}`}>Category</label>
                  <select
                    id={`${categoryId}-${line.key}`}
                    value={line.accountId}
                    onChange={(e) => updateSplitLine(line.key, { accountId: e.target.value })}
                  >
                    <option value="">Choose a category</option>
                    {categoryAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.number} — {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="register-entry__field">
                  <label htmlFor={`${amountId}-${line.key}`}>Amount</label>
                  <input
                    id={`${amountId}-${line.key}`}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={line.amount}
                    onChange={(e) => updateSplitLine(line.key, { amount: e.target.value })}
                  />
                </div>
                <RegisterQuantityInputs quantity={line.quantity} unit={line.unit}
                  onQuantityChange={(value) => updateSplitLine(line.key, { quantity: value })}
                  onUnitChange={(value) => updateSplitLine(line.key, { unit: value })}
                  quantityErrors={fieldErrors?.[`lines[${index}].quantity`]}
                  unitErrors={fieldErrors?.[`lines[${index}].unit`]} />
                {splitLines.length > 1 && (
                  <button
                    type="button"
                    className="btn btn--ghost register-entry__remove-line"
                    onClick={() => removeSplitLine(line.key)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <div className="register-entry__split-footer">
              <button type="button" className="btn btn--ghost" onClick={addSplitLine}>
                Add line
              </button>
              <output className="register-entry__split-total" aria-live="polite">
                Split total: {formatMoney(splitDistributed)}
                {splitLines.length < 2 && " — add another line to submit"}
              </output>
            </div>
          </div>
        )}

        <div className="register-entry__row">
          <div className="register-entry__field">
            <label htmlFor={occurredOnId}>Date</label>
            <input
              id={occurredOnId}
              type="date"
              value={occurredOn}
              onChange={(e) => setOccurredOn(e.target.value)}
              aria-invalid={fieldErrors?.occurredOn ? true : undefined}
            />
          </div>
        </div>

        <div className="register-entry__field">
          {/* Common to every mode, same as Memo below — ef-042-5's cross-slice assertion
              (`books-vendors.spec.ts`) reaches this picker from the default "simple" mode,
              but a Transfer or Split can carry a payee too. Labeled "Vendor" rather than
              "Payee" to match `Vendor` as the entity's own name everywhere else in this
              app (VendorRepository, the Vendors tab) — the e2e spec's own accessible-name
              check (`/vendor|payee/i`) accepts either.

              NOT a plain `<select>`, unlike every other picker in this form — measured
              directly (2026-09-20): a closed native `<select>`'s `<option>` elements have
              no layout box at all (`boundingBox()` returns null) until the dropdown is
              opened, so `books-vendors.spec.ts`'s own cross-slice assertion — the freshly
              created vendor's option is `toBeVisible()` right after a reload, with no
              click to open anything first — can never pass one. A hand-rolled
              combobox/listbox pair, always expanded, is what keeps the option a real,
              visible, DOM-nested element the moment the vendor list loads. Same tradeoff
              `PickerGrid`'s own remarks document for its chips: individually
              Tab-reachable, Enter/Space-activatable `role="option"` rows rather than the
              WAI-ARIA APG's full roving-tabindex arrow-key model — a true guarantee,
              not an unimplemented promise. */}
          <label id={vendorLabelId}>Vendor</label>
          <div
            role="combobox"
            aria-expanded="true"
            aria-haspopup="listbox"
            aria-controls={vendorListboxId}
            aria-labelledby={vendorLabelId}
            aria-invalid={fieldErrors?.vendorId ? true : undefined}
            className="register-entry__vendor-combobox"
          >
            <ul
              id={vendorListboxId}
              role="listbox"
              aria-labelledby={vendorLabelId}
              className="register-entry__vendor-listbox"
            >
              <VendorOption label="No vendor" selected={vendorId === ""} onSelect={() => setVendorId("")} />
              {vendors.map((v) => (
                <VendorOption
                  key={v.id}
                  label={v.name}
                  selected={vendorId === v.id}
                  onSelect={() => setVendorId(v.id)}
                />
              ))}
            </ul>
          </div>
        </div>

        <div className="register-entry__field">
          <label htmlFor={memoId}>Memo</label>
          <input
            id={memoId}
            type="text"
            // Matches the server's own cap (`BooksRegisterEndpoints.MaxMemoLength`) — a
            // wider client limit would let a farmer type past what the server accepts,
            // for a save that then 400s on a field this input never told them about.
            maxLength={200}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            aria-invalid={fieldErrors?.memo ? true : undefined}
          />
        </div>

        {formError && (
          <p className="register-entry__form-error" role="alert">
            {formError}
          </p>
        )}

        <div className="register-entry__actions">
          <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
            {saving ? "Recording…" : "Record"}
          </button>
        </div>
      </fieldset>
      </form>
    </li>
  );
}

/** One row of the Vendor listbox — see the field's own remarks in `RegisterEntryDialog`
 *  for why this is a hand-rolled `role="option"`, not a native `<select><option>`. */
function VendorOption({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li
      role="option"
      aria-selected={selected}
      tabIndex={0}
      className={`register-entry__vendor-option${selected ? " register-entry__vendor-option--selected" : ""}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        // Enter/Space activation, same as a native button — no arrow-key roving (see the
        // field's own remarks: an honest subset of the listbox keyboard model, not an
        // unimplemented promise of the full one).
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {label}
    </li>
  );
}
